import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Modal } from '@components/index';
import { getPetSelector } from '@modules/subscriptions/core/selectors';
import { PetProfileUpdateModal } from '../pet-profile-update/PetProfileUpdateModal';
import { usePetUpdateRequired, useMarkCompletedOnUpdateSuccess } from './usePetUpdateRequired';
import { PET_UPDATE_REQUIRED_DISMISSED_EVENT, getDismissedStorageKey } from './constants';
import { readStorageFlag, writeStorageFlag } from './utils';

/**
 * Intro announcement for customers flagged `update-required`. Pops on first
 * load and, on confirm, opens the recalculation flow (which re-runs the
 * calorie/product calculation with the pet's current data, shows the
 * before/after changes, and performs `update_pet_and_swap_line`).
 *
 * Dismissing without updating hands off to <PetUpdateIssueBanner />, which
 * keeps reminding the customer everywhere until they complete the update.
 */
export const PetUpdateIssueModal: React.FC = () => {
  const pet = useSelector(getPetSelector);
  const isPuppy = pet?.dog_stage === 'puppy';
  const { shouldShow: updateRequired, petId, markCompleted } = usePetUpdateRequired();

  const shouldShow = updateRequired && isPuppy;

  const [dismissed, setDismissed] = useState(false);
  const [showRecalcModal, setShowRecalcModal] = useState(false);

  useMarkCompletedOnUpdateSuccess(showRecalcModal, markCompleted);

  const storageKey = useMemo(() => (petId != null ? getDismissedStorageKey(petId) : null), [petId]);

  // Intro is one-time: once dismissed (persisted), the banner takes over.
  const alreadyDismissed = useMemo(() => readStorageFlag(storageKey), [storageKey]);

  const isIntroOpen = shouldShow && !dismissed && !alreadyDismissed && !showRecalcModal;

  const handleDismiss = () => {
    setDismissed(true);
    writeStorageFlag(storageKey);
    // Reveal the persistent banner.
    window.dispatchEvent(new CustomEvent(PET_UPDATE_REQUIRED_DISMISSED_EVENT));
  };

  const handleConfirm = () => {
    setShowRecalcModal(true);
  };

  const handleRecalcModalClose = () => {
    setShowRecalcModal(false);
    // On a successful update `markCompleted` hides everything; otherwise fall
    // back to the banner so the reminder persists.
    handleDismiss();
  };

  if (!shouldShow) return null;

  return (
    <>
      <Modal isOpen={isIntroOpen} handleClose={handleDismiss}>
        <div className="growth-phase-reminder-modal">
          <h3 className="growth-phase-reminder-modal__title pt-16">
            <FormattedMessage id="PET_UPDATE.MODAL.TITLE" />
          </h3>
          <p className="growth-phase-reminder-modal__text">
            <FormattedMessage
              id="PET_UPDATE.NEEDED_TEXT"
              values={{ gender: pet?.gender === 'f' ? 'f' : 'm' }}
            />
            <br />
            <br />
            <FormattedMessage id="PET_UPDATE.MODAL.EXPLAIN" />
          </p>
          <div className="growth-phase-reminder-modal__actions">
            <button type="button" className="recipe-btn active" onClick={handleConfirm}>
              <span>
                <FormattedMessage id="PET_UPDATE.CTA" />
              </span>
            </button>
          </div>
        </div>
      </Modal>

      {/* Recalculation flow — current data, before/after, then swap line */}
      <PetProfileUpdateModal
        isOpen={showRecalcModal}
        onClose={handleRecalcModalClose}
        mode="recalculation"
      />
    </>
  );
};
